import * as React from 'react'
import { StoreProps, castingStoreDisplayPriceWithTax, castingStoreGetCustomerBoughtCouponsPackInsights } from '../../../../services/stores/casting.store'
import { websiteCastingWidgetSigninUrl } from '../../../../services/models/website-casting-widgets.service'
import { I18nService } from '../../../../services/utils/i18n.service'
import SelectableListComponent from '../../../shared/selectable-list.component'
import { PrimaryButton } from '../casting-styled.component'
import ProgressBarComponent from '../../../shared/progress-bar.component'
import { theme } from '../../../../theme'
import { Currency, currencyFormat, currencyWrapTaxNotice } from '../../../../services/utils/currency.service'
import CastingTooltipButtonComponent from '../casting-tooltip-button.component'

const i18nScoped = new I18nService(require('./casting-coupon-picker.component.yml'))

export default function CastingCouponPickerComponent ({ store, data: { offeredService, coupon, renderingMode, customer, market } }: StoreProps) {
  const skipCouponsPack = React.useCallback(() => {
    store.setCoupon(null) // mark casting as do not want coupons packs for next events
    store.nextFocus({ closeOnMobile: true })
  }, [store])

  const agreeToUseAlreadyPaidCouponsPacks = React.useCallback(() => {
    store.nextFocus({ closeOnMobile: true })
  }, [store])

  const { totalBoughtCouponsPacksUsed, totalBoughtCouponsPacksPlanned, totalBoughtCouponsPacksQuantity, remainingBoughtCouponsPacksQuantity } = React.useMemo(() => {
    return castingStoreGetCustomerBoughtCouponsPackInsights({ customer, offeredService })
  }, [customer, offeredService])

  const displayPriceWithTax = React.useMemo(() => {
    return castingStoreDisplayPriceWithTax({ customer, offeredService, market })
  }, [customer, market, offeredService])

  const requireCouponsPack = offeredService.require_coupons_pack && window.currentUser?.type !== 'Operator'

  return <>
    { remainingBoughtCouponsPacksQuantity <= 2 && <SelectableListComponent
      name="couponPack"
      value={ coupon }
      data={ offeredService.coupons_packs }
      formatter={ val => formatCouponName(val, market.currency, displayPriceWithTax) }
      onChange={ val => { store.setCoupon(val); store.nextFocus({ closeOnMobile: true }) } } /> }

    { totalBoughtCouponsPacksQuantity > 0 && <div className="boxPod u-mtm u-mbs u-pam">
      <div className="u-mbs">{ i18nScoped.t('customer_bought_coupons_pack') }</div>
      <div className="u-positionRelative" style={{ height: 10 }}>
        <ProgressBarComponent style={{ position: 'absolute', width: '100%' }} color={ theme.colors.clr_0_light } max={ totalBoughtCouponsPacksQuantity } value={ totalBoughtCouponsPacksUsed + totalBoughtCouponsPacksPlanned } />
        <ProgressBarComponent style={{ position: 'absolute', width: '100%', backgroundColor: 'transparent' }} max={ totalBoughtCouponsPacksQuantity } value={ totalBoughtCouponsPacksUsed } />
      </div>
      <span className="txtSmall">{ i18nScoped.t('used_bought_coupons_pack', { count: totalBoughtCouponsPacksUsed + totalBoughtCouponsPacksPlanned, quantity: totalBoughtCouponsPacksQuantity }) }</span>
      &nbsp;•&nbsp;
      <span className="txtSmall">{ i18nScoped.t('remaining_bought_coupons_pack', { count: remainingBoughtCouponsPacksQuantity }) }</span>
    </div> }

    <div style={{ paddingTop: '1em', textAlign: 'center' }}>
      { requireCouponsPack && !window.currentUser && <p>{ i18nScoped.t('have_a_pack') } <a href={ websiteCastingWidgetSigninUrl(market) }>{ i18nScoped.t('sign_in_link') }</a></p> }
      { !requireCouponsPack && remainingBoughtCouponsPacksQuantity <= 0 && <PrimaryButton style={{ display: 'block' }} onClick={ skipCouponsPack }>{ i18nScoped.t('no_coupon') }</PrimaryButton> }
      { totalBoughtCouponsPacksQuantity > 0 && <p>{ i18nScoped.t('use_already_paid_coupons_pack') }</p> }
      { (requireCouponsPack || totalBoughtCouponsPacksQuantity > 0) && <CastingTooltipButtonComponent
        id="js-test-tooltip-next"
        renderingMode={ renderingMode }
        disabled={ store.getProgressForStep('coupon') !== 100 }
        onClick={ agreeToUseAlreadyPaidCouponsPacks }
        children={ i18nScoped.t('next') } /> }
    </div>
  </> 
}

function formatCouponName (coupon: StoreProps['data']['coupon'], currency: Currency, displayPriceWithTax: boolean, withValidity = true) {
  const displayName = `${coupon.name}: ${i18nScoped.t('hours', { count: coupon.quantity })}`
  const couponDisplayedPriceCents = coupon[displayPriceWithTax ? 'price_with_tax_cents' : 'price_without_tax_cents']

  return <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5em' }}>
    <div className="u-txtTruncate u-txtLeft">
      <span>{ displayName }</span><br/>
      <span style={{ fontSize: '0.8em' }}>{ withValidity && (coupon.validity_duration_in_months ? i18nScoped.t('valid', { count: coupon.validity_duration_in_months }) : i18nScoped.t('unlimited_valid')).replace(/ /g, '\u00A0') }</span>

      { coupon.partial_payment_percentage !== 100 && <div style={{ fontSize: '0.8em' }}>
        { i18nScoped.t('partial_payment_notice', {
          percentage: coupon.partial_payment_percentage,
          amount: currencyFormat(currency, (couponDisplayedPriceCents * coupon.partial_payment_percentage / 100 / 100).toFixed(2))
        }) }
      </div> }
    </div>

    <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
      <span style={{ backgroundColor: theme.colors.clr_1, color: 'white', fontWeight: 'bold', borderRadius: '1em', padding: '0.25em 0.5em' }}>
        { currencyWrapTaxNotice(currencyFormat(currency, (couponDisplayedPriceCents / 100).toFixed(2)), displayPriceWithTax) }
      </span>
    </div>
  </div>
}

export function formatCouponNameAsString (coupon: StoreProps['data']['coupon'], withValidity = true) {
  let displayName = `${coupon.name}: ${i18nScoped.t('hours', { count: coupon.quantity })}`

  if (withValidity) {
    displayName += ' - '
    displayName += (coupon.validity_duration_in_months ? i18nScoped.t('valid', { count: coupon.validity_duration_in_months }) : i18nScoped.t('unlimited_valid')).replace(/ /g, '\u00A0')
  }

  // Fix mobile display by adding non-breaking spaces
  return displayName.replace(/ /g, '\u00A0')
}
